
import React, { useEffect, useState } from 'react';
import useUpgradeDialog from '../../../hooks/useUpgradeDialog';
import useUpgradePrices from '../../../hooks/useUpgradePrices';
import UpgradePrice from '../UpgradePrice';
import { useLexicon } from '../../../hooks/useLexicon';
import { upgradeCheckout, getReferralStatus } from '../../../service';
import { FREE_MONTHLY_CREDITS, getBillingCycle } from '../../../utils/userAccessHelper';
import {
  ModalOverlay,
  ModalContainer,
  CloseButton,
  UpgradeLabel,
  ModalTitle,
  PlanGrid,
  PlanCard,
  PlanHeader,
  PlanLabel,
  AllInclusiveLabel,
  PromoLabel,
  PlanName,
  PlanSubtitle,
  PlanPrice,
  PlanToggle,
  ToggleButton,
  PlanDescription,
  PlanButton,
  PlanFeatures,
  FeatureItem,
  FeatureIcon,
  BackgroundPattern
} from './style';
import darkCheckCircle from '../../../static/icons/dark-check-circle.svg';
import referralIcon from '../../../static/icons/referral-icon.svg';
import TeacherReferralPopup from '../TeacherReferralPopup';
import CouponCode from '../CouponCode';
import SchoolPlanEnquiry from '../SchoolPlanEnquiry';
import { track as trackProductEvent } from '../../../utils/productEvents';

const TeacherPlanUpgradePopup = ({ isOpen, onClose, onPlanSelect, upgradeOptions = [], currentPlan = 'free', showAllPlans = false, profile = null, eyebrow = 'Upgrade', title = 'Upgrade your plan', subtitle = null, onContinueFree = null, eventTrigger = 'other' }) => {
  const { lt } = useLexicon();
  const [schoolBillingCycle, setSchoolBillingCycle] = useState('annually');
  const [proBillingCycle, setProBillingCycle] = useState('annually');
  const [showReferralPopup, setShowReferralPopup] = useState(false);
  const [showCouponCode, setShowCouponCode] = useState(false);
  const [referralResult, setReferralResult] = useState(null);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [isLoadingOffer, setIsLoadingOffer] = useState(false);
  // "Request a quote" opens the school plan enquiry form in place of the plans (it emails the sales
  // inbox as it opens, so a teacher who closes it is still followed up).
  const [showSchoolEnquiry, setShowSchoolEnquiry] = useState(false);
  const pricing = useUpgradePrices(isOpen);
  const [upgradeError, setUpgradeError] = useState(null);
  const dialogRef = useUpgradeDialog(isOpen && !showReferralPopup && !showCouponCode && !showSchoolEnquiry, onClose);
  // Callers keep this mounted and toggle isOpen, so each opening starts without the previous
  // attempt's error or busy flags (a School quote request used to leave its button disabled).
  useEffect(() => {
    if (!isOpen) return;
    setUpgradeError(null);
    setIsLoadingOffer(false);
    setShowSchoolEnquiry(false);
    trackProductEvent('paywall_shown', { trigger: eventTrigger, audience: 'teacher' });
  }, [isOpen]);
  const trackPaywallClick = (action, extra = {}) =>
    trackProductEvent('paywall_clicked', {
      trigger: eventTrigger,
      audience: 'teacher',
      action,
      ...extra,
    });
  if (!isOpen) return null;

  const showFreeCard = showAllPlans || currentPlan === 'free';
  const showSchoolCard = true;
  const showProCard = showAllPlans || currentPlan === 'pro' || upgradeOptions.includes('TEACHER_TO_PRO');

  const handlePlanSelect = async (planType) => {
    trackPaywallClick(planType === 'school' ? 'school_plan' : 'plan_selected', {
      plan_type: planType === 'school' ? 'school' : 'pro',
      billing_cycle: planType === 'school' ? schoolBillingCycle : proBillingCycle,
    });
    if (planType === 'pro') {
      setUpgradeError(null);
      setIsLoadingOffer(true);
      try {
        const referralStatus = await getReferralStatus();
        if (referralStatus && referralStatus.couponCode) {
          setReferralResult(referralStatus);
          setShowCouponCode(true);
        } else {
          setShowReferralPopup(true);
        }
      } catch (error) {
        setUpgradeError('We could not load your referral offer. Please try again.');
      } finally {
        setIsLoadingOffer(false);
      }
    } else if (planType === 'school') {
      setShowSchoolEnquiry(true);
    } else {
      await upgradeCheckout();
      onClose();
    }
  };

  const handleSchoolEnquiryClose = () => {
    setShowSchoolEnquiry(false);
    onClose?.();
  };

  const handleReferralClose = () => {
    setShowReferralPopup(false);
  };

  const handleReferralSendInvites = (result) => {
    setReferralResult(result);
    setShowReferralPopup(false);
    setShowCouponCode(true);
  };

  const handleCouponClose = () => {
    setShowCouponCode(false);
    setReferralResult(null);
    setIsRedirecting(false);
    if (onClose) {
      onClose();
    }
  };

  const handleReferralSkip = async () => {
    setIsRedirecting(true);
    try {
      await upgradeCheckout('TEACHER_PRO', proBillingCycle);
    } catch (error) {
      setIsRedirecting(false);
      throw error;
    }
  };

  const handleCouponCopy = (couponCode) => {
    console.log('Coupon code copied:', couponCode);
  };

  const handleCouponUpgrade = async() => {
    trackPaywallClick('coupon', { plan_type: 'pro', billing_cycle: proBillingCycle });
    setIsRedirecting(true);
    // Honour the plan the teacher actually selected (annually/monthly) and carry
    // the earned referral coupon through to checkout so it auto-applies.
    try {
      await upgradeCheckout('TEACHER_PRO', proBillingCycle, referralResult?.couponCode);
    } catch (error) {
      setIsRedirecting(false);
      throw error;
    }
  };

  const getSchoolBillingCycle = () => {
    if (profile && currentPlan === 'school') {
      return getBillingCycle(profile);
    }
    return schoolBillingCycle;
  };

  const getProBillingCycle = () => {
    if (profile && currentPlan === 'pro') {
      return getBillingCycle(profile);
    }
    return proBillingCycle;
  };



  return (
    <>
      {(showCouponCode || (isRedirecting && referralResult?.couponCode)) && referralResult?.couponCode ? (
        <CouponCode
          onCopy={handleCouponCopy}
          onUpgrade={handleCouponUpgrade}
          couponCode={referralResult.couponCode}
          onClose={handleCouponClose}
        />
      ) : null}
      
      {(showReferralPopup || (isRedirecting && !showCouponCode && !referralResult?.couponCode)) && (
        <TeacherReferralPopup
          isOpen={showReferralPopup || (isRedirecting && !showCouponCode && !referralResult?.couponCode)}
          onClose={handleReferralClose}
          onSendInvites={handleReferralSendInvites}
          onSkip={handleReferralSkip}
        />
      )}
      
      <SchoolPlanEnquiry
        isOpen={isOpen && showSchoolEnquiry}
        onClose={handleSchoolEnquiryClose}
        source="Upgrade popup"
        trigger={title}
      />

      {!showCouponCode && !showReferralPopup && !showSchoolEnquiry && (
    <ModalOverlay onClick={onClose}>
      <ModalContainer ref={dialogRef} role="dialog" aria-modal="true" aria-label="Upgrade your plan" tabIndex={-1} onClick={(e) => e.stopPropagation()}>
        <BackgroundPattern />
        <CloseButton aria-label="Close" onClick={() => { trackPaywallClick('dismissed'); onClose?.(); }}>×</CloseButton>
        
        <UpgradeLabel>{eyebrow}</UpgradeLabel>
        <ModalTitle>{title}</ModalTitle>
        {upgradeError && <p role="alert">{upgradeError}</p>}
        {subtitle && (
          <p style={{ margin: '4px 0 8px', textAlign: 'center', color: '#56515B', fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 15 }}>
            {subtitle}
          </p>
        )}

        <PlanGrid>
          {showFreeCard && (
            <PlanCard>
              <PlanHeader>
                <PlanName>Free</PlanName>
              </PlanHeader>
              <PlanDescription>{FREE_MONTHLY_CREDITS} free credits will be added to your account each month</PlanDescription>
              {onContinueFree ? (
                <PlanButton secondary onClick={(...args) => { trackPaywallClick('continue_free'); return onContinueFree?.(...args); }}>
                  Continue with Free
                </PlanButton>
              ) : !showAllPlans && currentPlan === 'free' ? (
                <PlanButton disabled>Current plan</PlanButton>
              ) : (
                <PlanButton secondary onClick={() => handlePlanSelect('free')}>
                  Start Free Trial
                </PlanButton>
              )}
              <PlanFeatures>
                <FeatureItem>
              <FeatureIcon><img src={darkCheckCircle} alt="resolve tick" /></FeatureIcon>
                  {FREE_MONTHLY_CREDITS} credits per month
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><img src={darkCheckCircle} alt="resolve tick" /></FeatureIcon>
                  AI-assisted feedback
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><img src={darkCheckCircle} alt="resolve tick" /></FeatureIcon>
                  Curriculum-aligned rubrics
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><img src={darkCheckCircle} alt="resolve tick" /></FeatureIcon>
                  Comment banks
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><img src={darkCheckCircle} alt="resolve tick" /></FeatureIcon>
                  Teacher customisations
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><img src={darkCheckCircle} alt="resolve tick" /></FeatureIcon>
                  Handwritten uploads
                </FeatureItem>
              </PlanFeatures>
            </PlanCard>
          )}

          {showSchoolCard && (
            <PlanCard>
              <PlanHeader>
                <AllInclusiveLabel>All Inclusive</AllInclusiveLabel>
                <PlanName>School</PlanName>
                <PlanSubtitle>Custom Pricing</PlanSubtitle>
              </PlanHeader>
              <PlanDescription>Add your classes, give feedback effortlessly</PlanDescription>
              {!showAllPlans && currentPlan === 'school' ? (
                <PlanButton disabled>Current plan</PlanButton>
              ) : (
                <PlanButton primary onClick={() => handlePlanSelect('school')} disabled={isRedirecting}>
                  Request a quote
                </PlanButton>
              )}
              <PlanFeatures>
                <FeatureItem>
                  <FeatureIcon><img src={darkCheckCircle} alt="resolve tick" /></FeatureIcon>
                  Full class access
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><img src={darkCheckCircle} alt="resolve tick" /></FeatureIcon>
                  Unlimited drafts submissions
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><img src={darkCheckCircle} alt="resolve tick" /></FeatureIcon>
                  AI-assisted feedback
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><img src={darkCheckCircle} alt="resolve tick" /></FeatureIcon>
                  Control student AI usage
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><img src={darkCheckCircle} alt="resolve tick" /></FeatureIcon>
                  Curriculum-aligned rubrics
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><img src={darkCheckCircle} alt="resolve tick" /></FeatureIcon>
                  Comment banks
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><img src={darkCheckCircle} alt="resolve tick" /></FeatureIcon>
                  Teacher customisations
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><img src={darkCheckCircle} alt="resolve tick" /></FeatureIcon>
                  Task creation
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><img src={darkCheckCircle} alt="resolve tick" /></FeatureIcon>
                  Handwritten uploads
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><img src={darkCheckCircle} alt="resolve tick" /></FeatureIcon>
                  Self-assessment
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><img src={darkCheckCircle} alt="resolve tick" /></FeatureIcon>
                  Peer review
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><img src={darkCheckCircle} alt="resolve tick" /></FeatureIcon>
                  Exemplars
                </FeatureItem>
                <FeatureItem>
                          <FeatureIcon><img src={darkCheckCircle} alt="resolve tick" /></FeatureIcon>
                  Class insights
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><img src={darkCheckCircle} alt="resolve tick" /></FeatureIcon>
                  LMS integrations
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><img src={darkCheckCircle} alt="resolve tick" /></FeatureIcon>
                  Onboarding and training
                </FeatureItem>
                <FeatureItem>
                    <FeatureIcon><img src={darkCheckCircle} alt="resolve tick" /></FeatureIcon>
                  24/7 Support
                </FeatureItem>
              </PlanFeatures>
            </PlanCard>
          )}

          {showProCard && (
            <PlanCard>
              <PlanHeader>
                <PromoLabel>
                  <img src={referralIcon} alt="referral icon" /> Refer teachers for 50% off your first payment</PromoLabel>
                <PlanName>Pro</PlanName>
              </PlanHeader>
              <UpgradePrice pricing={pricing} plan="TEACHER_PRO" cycle={getProBillingCycle()} PriceComponent={PlanPrice} />
              {(showAllPlans || currentPlan !== 'pro') && (
                <PlanToggle>
                  <ToggleButton 
                    active={proBillingCycle === 'annually'} 
                    onClick={() => setProBillingCycle('annually')}
                  >
                    Annually
                  </ToggleButton>
                  <ToggleButton 
                    active={proBillingCycle === 'monthly'} 
                    onClick={() => setProBillingCycle('monthly')}
                  >
                    Monthly
                  </ToggleButton>
                </PlanToggle>
              )}
              <PlanDescription>{lt('Mark student work in seconds with personalised AI feedback')}</PlanDescription>
              {!showAllPlans && currentPlan === 'pro' ? (
                <PlanButton disabled>Current plan</PlanButton>
              ) : (
                <PlanButton secondary onClick={() => handlePlanSelect('pro')} disabled={isRedirecting || isLoadingOffer || pricing.loading || !pricing.price('TEACHER_PRO', proBillingCycle)}>
                  Get Pro
                </PlanButton>
              )}
              <PlanFeatures>
                <FeatureItem>
                  <FeatureIcon><img src={darkCheckCircle} alt="resolve tick" /></FeatureIcon>
                  Unlimited drafts
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><img src={darkCheckCircle} alt="resolve tick" /></FeatureIcon>
                  AI-assisted feedback
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><img src={darkCheckCircle} alt="resolve tick" /></FeatureIcon>
                  Curriculum-aligned rubrics
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><img src={darkCheckCircle} alt="resolve tick" /></FeatureIcon>
                  Comment banks
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><img src={darkCheckCircle} alt="resolve tick" /></FeatureIcon>
                  Teacher customisations
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><img src={darkCheckCircle} alt="resolve tick" /></FeatureIcon>
                  Handwritten uploads
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><img src={darkCheckCircle} alt="resolve tick" /></FeatureIcon>
                  Priority support
                </FeatureItem>
              </PlanFeatures>
            </PlanCard>
          )}
        </PlanGrid>

      </ModalContainer>
    </ModalOverlay>
    )}
    </>
    );
};

export default TeacherPlanUpgradePopup;
